import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Persistent back button — appears on every page EXCEPT Landing/Home.
 * Uses browser history when available, falls back to a sensible default route
 * based on the current path so users never get stranded.
 */
const FALLBACK_BY_PATH = [
  { match: /^\/tracks\/[^/]+$/, to: "/tracks" },           // track detail → tracks list
  { match: /^\/tracks$/, to: "/dashboard" },                // tracks list → dashboard
  { match: /^\/facilitator\/[^/]+$/, to: "/facilitator" },  // participant detail → list
  { match: /.*/, to: "/dashboard" },                        // everything else → dashboard
];

export default function BackButton({ className = "" }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Don't render on the Landing page
  if (location.pathname === "/") return null;

  const onBack = () => {
    // Use history only when we have a real in-app referrer; otherwise we'd land
    // on about:blank in new-tab/deep-link visits. Same-origin referrer means
    // we came from inside the app.
    const sameOriginReferrer =
      typeof document !== "undefined" &&
      document.referrer &&
      (() => {
        try { return new URL(document.referrer).origin === window.location.origin; }
        catch { return false; }
      })();
    if (sameOriginReferrer && window.history.length > 1) {
      navigate(-1);
      return;
    }
    const fb = FALLBACK_BY_PATH.find((r) => r.match.test(location.pathname));
    navigate(fb ? fb.to : "/");
  };

  return (
    <button
      onClick={onBack}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#C9A961]/35 bg-[#0A0908]/40 text-[#C9A961] hover:bg-[#C9A961]/10 hover:border-[#C9A961]/65 transition text-sm font-medium shadow-sm ${className}`}
      aria-label="Go back to previous page"
      data-testid="global-back-btn"
    >
      <ArrowLeft className="w-4 h-4" />
      <span>Back</span>
    </button>
  );
}
